import request, { Response, ResponseError } from 'superagent';
import { updateModalWindow } from 'root/modal/modalWindowActions';
import { Country } from 'world-countries';
import { RegistrationConfirmation, RegistrationSyncData, RegistrationPaymentType } from 'features/registration-v2/registrationV2Slice';
import { LicensePlatePermission, ModalEnum, RegistrationFlowType } from 'myParkdUtils/enums';
import {
    CarBrand,
    CarModel,
    Company,
    CompanyDataFromVAT,
    CreditCardDetails,
    DbAction,
    DeleteDeviceErrorKey,
    DeviceMode,
    EditableCompanyDetails,
    EmptyApiResponse,
    Env,
    ErrorState,
    LocalizedLicensePlate,
    MinimalUser,
    PayloadState,
    QueryParams,
    RegistrationCompanyData,
    RegistrationPaymentData,
    RegistrationUserData,
    RegistrationVehicleData,
    RegistrationVehicleDataV2,
    ResponseState,
    SessionsApiResponse,
    SupportMessage,
    TeamleaderCreationRequest,
    TeamleaderCreationResponse,
    TelematicProviderName,
    TelematicProviderTypeMetadata,
    Token,
    TwikeySignFormResponse,
    User,
    UserCreationResponse,
    UserDTO,
    UserLoginResponse,
    UsersResponse,
    UserVehiclesResponse,
    Vehicle,
    WorkingWeek,
    FeaturePermission,
    TeamleaderSubscriptionResponse,
    ParkdCardsResponse,
    TeamLeaderSubscriptionMetaDataResponse,
    RegistrationResult
} from 'types';


const timeout = 30000;

class ParkdApiDefaultExportInstance {
    initialized!: boolean;
    customHeaders!: { Accept: string; 'x-app-sales-channel': string };
    endpoint!: string;
    myParkdServiceEndPoint!: any;
    store: any;

    init({ name = 'defaultAPIClient', version = '1.0', env = 'dev' }: { name: string; version: string; env: Env }) {
        this.initialized = true;
        this.setEndpoint(env);
        this.customHeaders = {
            Accept: 'application/json',
            'x-app-sales-channel': 'PARKD',
        };
    }

    setEndpoint = (env: Env) => {
        if (import.meta.env.VITE_API_ENDPOINT !== undefined) {
            this.endpoint = import.meta.env.VITE_API_ENDPOINT;
            this.myParkdServiceEndPoint = import.meta.env.VITE_API_MYPARKD_ENDPOINT;
        } else {
            if (env === 'dev') {
                this.endpoint = 'https://api.dev.parkd.com';
                this.myParkdServiceEndPoint = 'https://myparkd-service-api.dev.parkd.com';
            } else if (env === 'prod') {
                this.endpoint = 'https://api.parkd.com';
                this.myParkdServiceEndPoint = 'https://myparkd-service-api.parkd.com';
            } else if (env === 'localhost') {
                this.endpoint = 'http://localhost:8080';
            } else {
                this.endpoint = env;
            }
        }
    };

    responseHandler(resolve: Function, reject: Function, err: ResponseError, res: Response, httpMethod: string, path: string) {
        if (!err) {
            if (res.body) {
                let data = res.body.data;
                if (data == null) {
                    data = res.body;
                }
                resolve(data);
            } else {
                if (res.text) {
                    resolve(res.text);
                }
                resolve();
            }
        } else {
            reject(err); // todo: show specific timeout message to user
            /*
            reject({
                status: err.status,
                response: err.response,
                timeout: err.timeout
            });
            */
        }
    }

    checkInitialized() {
        if (this.initialized) {
            return;
        }
        throw new Error('Api was not initialized');
    }

    sleep(ms: number) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    // PUBLIC ENDPOINTS WITHOUT TOKEN ========================================================

    login(email: string, password: string): Promise<UserLoginResponse> {
        this.checkInitialized();
        return this.post('auth/login', null!, { email, password });
    }

    resetPassword(email: string) {
        return this.post('auth/password-reset/request', null!, { email: email });
    }

    createUser(user: RegistrationUserData): Promise<UserCreationResponse> {
        return this.post('users', null!, user);
    }

    getCarBrands(): Promise<CarBrand[]> {
        return this.get('oem/brands', null!);
    }

    getCarModels(brand: string): Promise<CarModel[]> {
        return this.get(`oem/brands/${brand}/models`, null!);
    }

    checkIfEmailExists(email: string): Promise<boolean> {
        return this.get(`users/exists?email=${email}`, null!);
    }

    getCompanyDetailsByVAT(vat: string, token?: string): Promise<CompanyDataFromVAT> {
        return this.get(`companies/vat/${vat}`, null!);
    }

    getAllTelematicProviderNames(): Promise<TelematicProviderName[]> {
        return this.get('telematicaccounts/all', null!);
    }

    getTeamLeaderSubscriptionMetaData(
        registrationFlowType: RegistrationFlowType,
        countryCode: Country['cca2'],
        numberOfCars: number
    ): Promise<TeamLeaderSubscriptionMetaDataResponse> {
        return this.get(`companies/teamleader-subscription/metadata/${registrationFlowType}/${countryCode}/${numberOfCars}`, null!);
    }

    createTeamleaderCompany(token: string, creationRequest: TeamleaderCreationRequest): Promise<TeamleaderCreationResponse> {
        return this.post('users/me/company/teamleader', token, creationRequest);
    }

    createTeamleaderSubscription(
        token: string,
        registrationFlowType: RegistrationFlowType,
        numberOfCars: number,
        paymentMethodType: RegistrationPaymentType
    ): Promise<TeamleaderSubscriptionResponse | undefined> {
        return this.post('users/me/company/v2/teamleader-subscription', token, { paymentMethodType, registrationFlowType, numberOfCars });
    }

    createTwikeyInvite(token: string, registrationId: string, paymentType: RegistrationPaymentType): Promise<TwikeySignFormResponse> {
        return this.get(`users/me/company/v2/twikey-invites/${registrationId}?subscriptionPaymentType=${paymentType}`, token);
    }

    syncUserAsHubspotCustomer(token: string, registrationDataForSync: RegistrationSyncData) {
        return this.post('users/me/company/track-creation', token, registrationDataForSync);
        // publishes an event to internal queue (no errors returned)
    }

    confirmRegistration(token: string, registrationConfirmation: RegistrationConfirmation): Promise<RegistrationResult> {
        return this.post('users/me/company/registration-complete', token, registrationConfirmation);
        // sends onboarding email + onboards company when telematicProvider is APP
    }

    updateSessionNote(token: Token, { sessionPublicId, note }: { sessionPublicId: string, note: string | null }) {
        return this.patch(`admin/v1/parkingrights/${sessionPublicId}/note`, token, { sessionNote: note });
    }

    updateCostCenter(token: Token, llp: LocalizedLicensePlate, companyPublicId: string, costCenter: string | null) {
        return this.patch(`admin/vehicles/${llp.countryCode}/${llp.licensePlate}/company-rights/${companyPublicId}/cost-center?costCenter=${costCenter}`, token);
    }

    /*
    // emailexits response
    {"errors":null,"data":false}
    Api Error when creating user that already exists
    createUser()
    /users
    {"errors":[{"errorKey":"PARKD_API_ERROR_EMAIL_ALREADY_EXISTS","errorMessage":"Email already exists"}],"data":null}
    */

    // =======================================================================================

    getUser(token: string): Promise<User> {
        return this.get('users/me', token);
    }

    saveUser(token: string, user: UserDTO): Promise<EmptyApiResponse> {
        return this.patch('users/me', token, user);
    }

    updatePassword(token: string, oldPassword: string, newPassword: string) {
        return this.patch('users/me', token, { oldPassword: oldPassword, password: newPassword });
    }

    createCompany(token: string, company: RegistrationCompanyData): Promise<number> {
        return this.post('users/me/company', token, company);
    }

    getCompanyDetails(token: string): Promise<Company> {
        return this.get('users/me/company', token);
    }

    createCompanyVehicles(token: string, vehicle: RegistrationVehicleData) {
        return this.post('users/me/company/webfleet-oem-devices', token, vehicle);
    }

    registerOemVehicles(token: string, vehicles: RegistrationVehicleDataV2[]): Promise<number> {
        return this.post('users/me/company/oem-devices', token, vehicles)
    }

    getCompanyVehicles(token: string, queryParams: QueryParams): Promise<UserVehiclesResponse> {
        return this.get(`users/me/company/vehicles${queryParams}`, token);
    }

    unclaimVehicle(token: Token, countryCode: Country['cca2'], licensePlate: string): Promise<any> {
        return this.post(`users/me/company/vehicles/${countryCode}/${licensePlate}/unclaim`, token);
    }

    deleteVehicle(token: Token, countryCode: Country['cca2'], licensePlate: string, skipValidations: boolean): Promise<undefined | DeleteDeviceErrorKey[]> {
        return this.post(`users/me/company/vehicles/${countryCode}/${licensePlate}/invalidate?skipValidations=${skipValidations}`, token);
    }

    getSessionsExportOnstreet(token: string, queryParams: QueryParams): Promise<Blob> {
        return this.get(`users/me/company/sessions/stopped/onstreet/report${queryParams}`, token, false, true);
    }

    getSessionsExportOffstreet(token: string, queryParams: QueryParams): Promise<Blob> {
        return this.get(`users/me/company/sessions/stopped/offstreet/report${queryParams}`, token, false, true);
    }

    getHistoricalSessions(token: string, queryParams: QueryParams): Promise<SessionsApiResponse> {
        return this.get(`users/me/company/stopped-sessions${queryParams}`, token);
    }

    getActiveSessions(token: string, queryParams: QueryParams): Promise<SessionsApiResponse> {
        return this.get(`users/me/company/started-business-sessions${queryParams}`, token);
    }

    getOffstreetSessions(token: string, queryParams: QueryParams): Promise<SessionsApiResponse> {
        return this.get(`users/me/company/sessions/stopped/offstreet/${queryParams}`, token);
    }

    getCompanyUsers(token: string, queryParams: QueryParams): Promise<UsersResponse> {
        return this.get(`users/me/company/users${queryParams}`, token);
    }

    updateCompanyUser(token: string, companyPublicId: string, user: MinimalUser) {
        return this.put(`users/v2/${companyPublicId}/${user.publicId}`, token, user);
    }

    deleteCompanyUser(token: string, companyPublicId: string, userPublicId: string) {
        return this.del(`users/v2/${companyPublicId}/${userPublicId}`, token);
    }

    updateCompanyDetails(token: string, companyDetails: EditableCompanyDetails) {
        return this.put('users/me/company', token, companyDetails);
    }

    // CALL API MANUALLY (in dropdown + importLP )
    getCompanyTelematicAccounts(token: string): Promise<TelematicProviderTypeMetadata[]> {
        return this.get('users/me/company/telematicaccounts', token);
    }

    getAllTelematicAccountTypes(token: string): Promise<TelematicProviderTypeMetadata[]> {
        return this.get('users/me/company/telematicaccounts/all', token);
    }

    validateEnteredLicensePlates(token: string, telematicAccountType: string, licensePlates: string) {
        return this.post(`users/me/company/telematicaccounts/${telematicAccountType}/importrequests/validations`, token, licensePlates);
    }

    importValidLicensePlates(token: string, telematicAccountType: string, proposedDbActions: DbAction[]) {
        return this.post(`users/me/company/telematicaccounts/${telematicAccountType}/importrequests/actions`, token, proposedDbActions);
    }

    getCompanyInvoices(token: string, companyId: number) {
        const isMyParkdServiceEndPoint = true;
        return this.get(`companies/${companyId}/invoices`, token, isMyParkdServiceEndPoint);
    }

    getCompanyParkdCards(token: string, queryParams: QueryParams): Promise<ParkdCardsResponse> {
        return this.get(`users/me/company/parking-cards${queryParams}`, token);
    }

    getCompanyFeaturePermissions(token: Token): Promise<FeaturePermission[]> {
        return this.get(`users/me/company/features`, token);
    }

    // JiraServiceManagement
    sendMessageToSupport(token: string, message: SupportMessage) {
        return this.post('support/feedback', token, message);
    }

    createPaymentMethod(token: string, paymentMethod: RegistrationPaymentData) {
        return this.post('users/me/company/payment-methods', token, paymentMethod);
    }

    // not yet implemented
    updatePaymentMethod(token: string, cardDetails: CreditCardDetails) {
        return this.put('users/me/company/payment-methods', token, cardDetails);
    }

    //=====================================================================
    // ADMIN endpoints
    //=====================================================================

    updateDeviceMode(token: Token, llp: LocalizedLicensePlate, deviceMode: DeviceMode) {
        return this.put(`admin/vehicles/${llp.countryCode}/${llp.licensePlate}/settings/mode?value=${deviceMode}`, token);
    }

    updateLicensePlatePermission(token: Token, companyPublicId: string, vehicle: Vehicle, permission: LicensePlatePermission): Promise<any> {
        const { countryCode, licensePlate } = vehicle.localizedLicensePlate;
        return this.put(`admin/vehicles/${countryCode}/${licensePlate}/company-rights/${companyPublicId}/session-start?permission=${permission}`, token, null);
    }

    stopSession(token: string, publicId: string) {
        return this.post(`admin/sessions/${publicId}/stop-command`, token);
    }

    updateLicensePlateWorkingHours = (token: Token, llp: LocalizedLicensePlate, companyPublicId: string, workingWeek: WorkingWeek | null) => {
        return this.patch(`admin/vehicles/${llp.countryCode}/${llp.licensePlate}/company-rights/${companyPublicId}/working-week`, token, workingWeek);
    }

    //=====================================================================

    async callApiWithGlobalErrorHandling(apiCall: Promise<Function>) {
        try {
            return await apiCall;
        } catch (err) {
            this.store.dispatch(updateModalWindow(ModalEnum.SOMETHING_WENT_WRONG, { apiError: err })); // how can this work when store is never set?
            return new Promise((resolve, reject) => {
                reject(err);
            });
        }
    }

    getHeaders(token: string) {
        if (token) {
            return {
                ...this.customHeaders,
                Authorization: `Bearer ${token}`,
            };
        } else {
            return this.customHeaders;
        }
    }

    patch(path: string, token: string, payload: PayloadState = null): Promise<any> {
        this.checkInitialized();
        return new Promise((resolve, reject) => {
            var fullPath = `${this.endpoint}/${path}`;
            request.patch(fullPath)
                .send(payload)
                .set(this.getHeaders(token))
                .timeout(timeout)
                .end((err: ErrorState, res: ResponseState) => {
                    this.responseHandler(resolve, reject, err, res, 'PATCH', path);
                });
        });
    }

    put(path: string, token: string, payload: PayloadState = null): Promise<any> {
        this.checkInitialized();
        return new Promise((resolve, reject) => {
            var fullPath = `${this.endpoint}/${path}`;
            request.put(fullPath)
                .send(payload)
                .set(this.getHeaders(token))
                .timeout(timeout)
                .end((err: ErrorState, res: ResponseState) => {
                    this.responseHandler(resolve, reject, err, res, 'PUT', path);
                });
        });
    }

    post(path: string, token: string, payload: PayloadState = null): Promise<any> {
        this.checkInitialized();
        return new Promise((resolve, reject) => {
            var fullPath = `${this.endpoint}/${path}`;
            request.post(fullPath)
                .send(payload)
                .set(this.getHeaders(token))
                .timeout(timeout)
                .end((err: ErrorState, res: ResponseState) => {
                    this.responseHandler(resolve, reject, err, res, 'POST', path);
                });
        });
    }

    get(path: string, token: string, isMyParkdServiceEndPoint = false, isBlob = false): Promise<any> {
        this.checkInitialized();
        return new Promise((resolve, reject) => {
            var fullPath = `${this.endpoint}/${path}`;
            if (isMyParkdServiceEndPoint) {
                fullPath = `${this.myParkdServiceEndPoint}/${path}`;
            }
            request.get(fullPath)
                .set(this.getHeaders(token))
                .responseType(isBlob ? 'blob' : '')
                .timeout(timeout)
                .end((err: ErrorState, res: ResponseState) => {
                    this.responseHandler(resolve, reject, err, res, 'GET', path);
                });
        });
    }

    del(path: string, token: string) {
        this.checkInitialized();
        return new Promise((resolve, reject) => {
            let fullPath = `${this.endpoint}/${path}`;
            request.delete(fullPath)
                .send()
                .set(this.getHeaders(token))
                .timeout(timeout)
                .end((err: ErrorState, res: ResponseState) => {
                    this.responseHandler(resolve, reject, err, res, 'DEL', path);
                });
        });
    }

}

export const Api = new ParkdApiDefaultExportInstance();